import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuthContext } from './AuthContext';
import { apiService } from '../services/api';

export type BusinessStatus = 'draft' | 'pending_approval' | 'approved' | 'rejected' | 'suspended';
type OnboardingStatus = 'loading' | 'none' | 'incomplete' | 'complete';

const WIRE_BUSINESS_STATUS: Record<string, BusinessStatus> = {
    Draft: 'draft',
    PendingApproval: 'pending_approval',
    Approved: 'approved',
    Rejected: 'rejected',
    // Batch C (AB#2195) — Devanza suspended the salon.
    Suspended: 'suspended',
    '0': 'draft',
    '1': 'pending_approval',
    '2': 'approved',
    '3': 'rejected',
    '4': 'suspended',
};

/**
 * AB#2108 (Admin V1 P0-2) — map the backend `status` from GET /onboarding/status
 * (BusinessStatus as its enum name, or its number) to the portal status that
 * drives the pending / rejected / suspended banner (Batch C, AB#2195). Unknown →
 * null (the caller keeps its fallback).
 */
export function toBusinessStatus(status: unknown): BusinessStatus | null {
    if (typeof status !== 'string' && typeof status !== 'number') return null;
    return WIRE_BUSINESS_STATUS[String(status)] ?? null;
}

interface OnboardingContextType {
    status: OnboardingStatus;
    businessStatus: BusinessStatus | null;
    rejectionReason: string | null;
    /** Batch C (AB#2195) — Devanza's reason, while the salon is suspended. */
    suspensionReason: string | null;
    currentStep: number;
    /**
     * AB#2108 — `GET /onboarding/status` failed, so `businessStatus` is unknown
     * rather than absent. Routing still treats the owner as complete (as before),
     * but surfaces that depend on the real status must not assume "approved":
     * that silently hid both the pending banner and the Submit card.
     */
    statusUnavailable: boolean;
    refreshOnboardingStatus: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isSalonOwner, businessId, isAuthenticated } = useAuthContext();
    const [status, setStatus] = useState<OnboardingStatus>('loading');
    const [businessStatus, setBusinessStatus] = useState<BusinessStatus | null>(null);
    const [rejectionReason, setRejectionReason] = useState<string | null>(null);
    const [suspensionReason, setSuspensionReason] = useState<string | null>(null);
    const [currentStep, setCurrentStep] = useState(0);
    const [statusUnavailable, setStatusUnavailable] = useState(false);

    const fetchStatus = useCallback(async () => {
        // Non-authenticated users: don't fetch, stay loading (RequireRole handles redirect)
        if (!isAuthenticated) {
            setStatus('loading');
            return;
        }

        // Non-SalonOwner roles never need onboarding
        if (!isSalonOwner) {
            setStatus('complete');
            setBusinessStatus('approved');
            setCurrentStep(0);
            return;
        }

        // SalonOwner with no businessId in JWT -> hasn't created a business yet
        if (businessId === null || businessId === undefined) {
            setStatus('none');
            setBusinessStatus(null);
            setCurrentStep(0);
            return;
        }

        // SalonOwner with businessId -> check API for completion
        try {
            setStatus('loading');
            const res = await apiService.getOnboardingStatus();
            setStatusUnavailable(false);
            // AB#2108 — the backend sends `status`; the old `businessStatus` field
            // never existed, so pending / rejected were always read as approved.
            const bStatus = toBusinessStatus(res.status) ?? (res.isFullyCompleted ? 'approved' : 'draft');
            setBusinessStatus(bStatus);
            setRejectionReason(res.rejectionReason ?? null);
            setSuspensionReason(res.suspensionReason ?? null);

            if (bStatus === 'approved') {
                setStatus('complete');
            } else if (bStatus === 'pending_approval') {
                // Owner finished wizard, waiting for admin — treat as "complete" for routing
                // (they should not be redirected to /onboarding)
                setStatus('complete');
            } else if (bStatus === 'rejected' || bStatus === 'suspended') {
                // Rejected / suspended — allow access to dashboard but show banner
                setStatus('complete');
            } else {
                // draft — still onboarding
                setStatus(res.isFullyCompleted ? 'complete' : 'incomplete');
            }
            // The backend has no step index (the old wizard's field was never sent).
            setCurrentStep(0);
        } catch {
            // If the endpoint fails (e.g. 404 for older backends), keep treating the
            // owner as complete so routing never traps them — but do NOT claim
            // "approved" (AB#2108): that hid the pending banner AND the Submit card,
            // leaving a Draft owner with no way to go live until a page reload.
            setStatus('complete');
            setBusinessStatus(null);
            setStatusUnavailable(true);
            setCurrentStep(0);
        }
    }, [isAuthenticated, isSalonOwner, businessId]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const refreshOnboardingStatus = useCallback(async () => {
        await fetchStatus();
    }, [fetchStatus]);

    return (
        <OnboardingContext.Provider value={{ status, businessStatus, rejectionReason, suspensionReason, currentStep, statusUnavailable, refreshOnboardingStatus }}>
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) throw new Error('useOnboarding must be used within OnboardingProvider');
    return context;
};
